/* /assets/bundle-builder.js
 * <bundle-builder> Web Component for Maison Mahj's "Build Your Custom Set" page.
 * Architecture decisions captured in research/03-architecture-decision.md.
 */
(function () {
  if (customElements.get('bundle-builder')) return;

  class BundleBuilder extends HTMLElement {
    constructor() {
      super();
      this.selections = new Map(); // categoryKey -> { variantId, price, image, title, soldOut, preorder, productHandle }
      this.options = []; // cached HTMLButtonElements
      this.boundDocClick = this.onDocumentClick.bind(this);
    }

    connectedCallback() {
      this.preview = this.querySelector('[data-preview]');
      this.placeholder = this.querySelector('[data-preview-placeholder]');
      this.totalEl = this.querySelector('[data-total-price]');
      this.errorEl = this.querySelector('[data-error]');
      this.hintEl = this.querySelector('[data-hint]');
      this.addBtn = this.querySelector('[data-add-bundle]');
      this.addText = this.querySelector('[data-add-text]');
      this.options = Array.from(this.querySelectorAll('[data-option]'));
      this.dropdowns = Array.from(this.querySelectorAll('[data-dropdown]'));

      this.options.forEach((opt) => {
        opt.addEventListener('click', (e) => this.onOptionClick(e));
      });
      this.addBtn?.addEventListener('click', (e) => this.onAddClick(e));
      document.addEventListener('click', this.boundDocClick);

      // Default total + button state
      this.refresh();

      // Deep-link pre-selection support: ?preselect=<handle> [&variant=<id>]
      this.handleDeepLink();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.boundDocClick);
    }

    onDocumentClick(e) {
      // Close any <details> dropdown when clicking outside one.
      this.dropdowns.forEach((d) => {
        if (d.open && !d.contains(e.target)) d.open = false;
      });
    }

    onOptionClick(e) {
      e.preventDefault();
      const btn = e.currentTarget;
      const cat = btn.dataset.category;
      const isClear = btn.dataset.clear === 'true';
      const wasSelected = btn.classList.contains('is-selected');

      // Deselect any other option in this category
      this.options
        .filter((o) => o.dataset.category === cat)
        .forEach((o) => {
          o.classList.remove('is-selected');
          o.setAttribute('aria-pressed', 'false');
        });

      if (isClear || wasSelected) {
        // Clear option, or re-clicking the already-selected product, both
        // return this category to the no-selection state.
        this.selections.delete(cat);
      } else {
        btn.classList.add('is-selected');
        btn.setAttribute('aria-pressed', 'true');
        this.selections.set(cat, {
          variantId: btn.dataset.variantId,
          price: parseInt(btn.dataset.price, 10) || 0,
          image: btn.dataset.image || '',
          title: btn.dataset.title || '',
          soldOut: btn.dataset.soldOut === 'true',
          preorder: btn.dataset.preorder === 'true',
          productHandle: btn.dataset.productHandle || '',
        });
      }

      // Close the dropdown after selection
      const dropdown = btn.closest('[data-dropdown]');
      if (dropdown) dropdown.open = false;

      this.refresh();
    }

    refresh() {
      this.refreshPreviewLayers();
      this.refreshDropdownTriggers();
      this.refreshTotal();
      this.refreshAddButton();
      this.refreshPlaceholder();
    }

    refreshPreviewLayers() {
      // For each layer, set or clear its src based on whether the matching category is selected.
      const layers = this.querySelectorAll('[data-preview-image]');
      layers.forEach((img) => {
        const cat = img.dataset.previewImage;
        const sel = this.selections.get(cat);
        if (sel && sel.image) {
          if (img.src !== sel.image) img.src = sel.image;
          img.removeAttribute('hidden');
          img.alt = sel.title;
        } else {
          img.setAttribute('hidden', '');
          img.removeAttribute('src');
          img.alt = '';
        }
      });
    }

    refreshPlaceholder() {
      if (!this.placeholder) return;
      if (this.selections.size > 0) {
        this.placeholder.setAttribute('hidden', '');
      } else {
        this.placeholder.removeAttribute('hidden');
      }
    }

    refreshDropdownTriggers() {
      const triggerContents = this.querySelectorAll('[data-trigger-content]');
      triggerContents.forEach((tc) => {
        const cat = tc.dataset.triggerContent;
        const sel = this.selections.get(cat);
        // Find the original label and price area
        const label = tc.querySelector('.bb__trigger-label');
        const hint = tc.querySelector('.bb__trigger-hint');
        let priceEl = tc.querySelector('.bb__trigger-price');
        let badge = tc.querySelector('.bb__trigger-badge');

        if (sel) {
          if (label) label.textContent = sel.title;
          if (hint) hint.style.display = 'none';
          if (!priceEl) {
            priceEl = document.createElement('span');
            priceEl.className = 'bb__trigger-price';
            tc.appendChild(priceEl);
          }
          priceEl.textContent = formatMoney(sel.price);
          // Sold-out badge inside trigger
          if (sel.soldOut) {
            if (!badge) {
              badge = document.createElement('span');
              badge.className = 'bb__trigger-badge bb__trigger-badge--sold-out';
              tc.appendChild(badge);
            }
            badge.textContent = i18n('sold_out_badge');
          } else if (badge) {
            badge.remove();
          }
          tc.closest('.bb__dropdown-trigger')?.classList.add('has-selection');
        } else {
          // Reset to default label
          if (hint) hint.style.display = '';
          if (priceEl) priceEl.remove();
          if (badge) badge.remove();
          if (label) {
            const original = tc.dataset.originalLabel || label.textContent;
            label.textContent = original;
          }
          tc.closest('.bb__dropdown-trigger')?.classList.remove('has-selection');
        }
      });
    }

    refreshTotal() {
      let total = 0;
      this.selections.forEach((s) => (total += s.price));
      if (this.totalEl) this.totalEl.textContent = formatMoney(total);
    }

    refreshAddButton() {
      if (!this.addBtn) return;
      const hasSelection = this.selections.size > 0;
      const soldOutCount = [...this.selections.values()].filter((s) => s.soldOut).length;

      this.addBtn.disabled = !hasSelection;

      // Visual + copy state
      if (!hasSelection) {
        this.addBtn.classList.remove('bb__add--blocked');
        this.setHint(i18n('hint_initial'));
        this.clearError();
      } else if (soldOutCount > 0) {
        this.addBtn.classList.add('bb__add--blocked');
        const titles = [...this.selections.values()].filter((s) => s.soldOut).map((s) => s.title);
        this.setHint(
          i18n('hint_sold_out', { count: soldOutCount, items: titles.join(', ') })
        );
      } else {
        this.addBtn.classList.remove('bb__add--blocked');
        this.setHint(i18n('hint_ready', { count: this.selections.size }));
        this.clearError();
      }
    }

    setHint(msg) {
      if (this.hintEl) this.hintEl.textContent = msg;
    }
    setError(msg) {
      if (!this.errorEl) return;
      this.errorEl.textContent = msg;
      this.errorEl.removeAttribute('hidden');
    }
    clearError() {
      if (!this.errorEl) return;
      this.errorEl.textContent = '';
      this.errorEl.setAttribute('hidden', '');
    }

    async onAddClick(e) {
      e.preventDefault();
      this.clearError();
      if (this.selections.size === 0) return;

      const soldOutItems = [...this.selections.values()].filter((s) => s.soldOut);
      if (soldOutItems.length > 0) {
        const titles = soldOutItems.map((s) => s.title).join(', ');
        this.setError(i18n('error_sold_out_in_set', { items: titles }));
        return;
      }

      const items = [...this.selections.values()].map((s) => {
        const item = { id: s.variantId, quantity: 1 };
        if (s.preorder) item.properties = { preorder: 'true' };
        return item;
      });

      const formData = new FormData();
      items.forEach((it, i) => {
        formData.append(`items[${i}][id]`, it.id);
        formData.append(`items[${i}][quantity]`, it.quantity);
        if (it.properties && it.properties.preorder) {
          formData.append(`items[${i}][properties][preorder]`, 'true');
        }
      });

      const originalText = this.addText?.textContent;
      if (this.addText) this.addText.textContent = i18n('adding');
      this.addBtn.disabled = true;

      try {
        const res = await fetch(window.theme?.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/javascript',
          },
          body: formData,
        });
        const data = await res.json();
        if (!res.ok || data.status) {
          throw new Error(data.description || data.message || 'Add to cart failed');
        }
        this.notifyCart(data);
      } catch (err) {
        this.setError(err.message || i18n('error_generic'));
      } finally {
        if (this.addText && originalText) this.addText.textContent = originalText;
        this.addBtn.disabled = this.selections.size === 0;
      }
    }

    notifyCart(data) {
      // Refresh cart drawer + theme pub/sub (Broadcast 8 uses both).
      document.dispatchEvent(new CustomEvent('theme:cart:refresh'));
      document.dispatchEvent(new CustomEvent('theme:cart:added', { detail: data, bubbles: true }));
      if (typeof window.publish === 'function') {
        try {
          window.publish('cart-update', data);
        } catch (e) {
          /* swallow — pub/sub may not be initialized */
        }
      }
      this.setHint(i18n('hint_added'));
    }

    handleDeepLink() {
      const params = new URLSearchParams(window.location.search);
      const paramHandle = params.get('preselect');
      const variantId = params.get('variant');
      const defaultHandle = this.dataset.defaultPreselect || '';

      // 1) Lay down the configured default product (e.g. a base mat) silently, so
      //    the canvas is never blank. Skip if it's the same product the deep link
      //    will select (avoids a toggle-off), or if it's already selected / missing.
      if (defaultHandle && defaultHandle !== paramHandle) {
        const def = this.options.find((o) => o.dataset.productHandle === defaultHandle);
        if (def && !def.classList.contains('is-selected')) def.click();
      }

      // 2) Apply the explicit deep-linked product on top. If it shares the base
      //    layer's category (e.g. another mat), selecting it replaces the default
      //    in that single slot; a different category stacks alongside the base.
      if (!paramHandle) return;
      let match = this.options.find(
        (o) => o.dataset.productHandle === paramHandle && (!variantId || o.dataset.variantId === variantId)
      );
      if (!match) match = this.options.find((o) => o.dataset.productHandle === paramHandle);
      if (!match) return;
      // Sold-out items are pre-selectable: the user can see them in the composite,
      // and the add-to-cart button blocks with a clear "X is sold out" hint (refreshAddButton).
      if (!match.classList.contains('is-selected')) match.click();
      // Tasteful pulse on the deep-linked layer (not the silent default).
      const cat = match.dataset.category;
      const layer = this.querySelector(`[data-layer="${cat}"]`);
      if (layer) {
        layer.classList.add('bb__layer--just-preselected');
        setTimeout(() => layer.classList.remove('bb__layer--just-preselected'), 700);
      }
    }
  }

  // --- helpers --------------------------------------------------------------

  function formatMoney(cents) {
    if (typeof window.theme?.formatMoney === 'function') {
      return window.theme.formatMoney(cents, window.theme?.moneyFormat);
    }
    if (typeof window.Shopify?.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
    }
    return `$${(cents / 100).toFixed(2)}`;
  }

  function i18n(key, opts = {}) {
    const strings = (window.theme && window.theme.bundleBuilderStrings) || {};
    let s = strings[key] || DEFAULTS[key] || key;
    Object.keys(opts).forEach((k) => {
      s = s.replace(new RegExp(`\\{${k}\\}`, 'g'), opts[k]);
    });
    return s;
  }

  const DEFAULTS = {
    sold_out_badge: 'Sold out',
    preorder_badge: 'Pre-order',
    hint_initial: 'Pick at least one item to get started.',
    hint_ready: 'Ready to add {count} items to your cart.',
    hint_sold_out: '{count} sold-out item(s) in your set: {items}. Remove or swap to continue.',
    hint_added: 'Set added to your cart.',
    adding: 'Adding...',
    error_sold_out_in_set: 'Some items are sold out: {items}. Remove or swap them to continue.',
    error_generic: 'Could not add to cart. Please try again.',
  };

  customElements.define('bundle-builder', BundleBuilder);
})();
